import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { articlesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Download, ArrowLeft, Calendar, Tag, ExternalLink, Volume2, Lock, CreditCard, Clock, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { ArticleFeedback } from '../components/ArticleFeedback';
import { RatingSummary } from '../components/StarRating';

function ScholarMeta({ article, journalInfo }) {
  useEffect(() => {
    if (!article) return;
    const metas = [
      { name: 'citation_title', content: article.titulo },
      { name: 'citation_author', content: article.autores },
      { name: 'citation_publication_date', content: article.published_at ? new Date(article.published_at).toISOString().split('T')[0].replace(/-/g, '/') : '' },
      { name: 'citation_journal_title', content: 'Archivos Latinoamericanos de Estudios Trans' },
      { name: 'citation_journal_abbrev', content: 'ALET' },
      { name: 'citation_volume', content: String(article.volumen || '') },
      { name: 'citation_issue', content: String(article.numero || '') },
      { name: 'citation_language', content: 'es' },
      { name: 'citation_abstract', content: article.abstract_es || '' },
    ];
    if (journalInfo?.issn) metas.push({ name: 'citation_issn', content: journalInfo.issn });
    if (article.doi) metas.push({ name: 'citation_doi', content: article.doi });
    if (article.file_name) metas.push({ name: 'citation_pdf_url', content: articlesAPI.downloadUrl(article.id) });
    // Keywords
    if (article.palabras_clave?.length) metas.push({ name: 'citation_keywords', content: article.palabras_clave.join('; ') });

    const elements = [];
    metas.forEach(({ name, content }) => {
      if (!content) return;
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      elements.push(meta);
    });

    // DC metadata for broader indexing
    const dcMetas = [
      { name: 'DC.title', content: article.titulo },
      { name: 'DC.creator', content: article.autores },
      { name: 'DC.type', content: 'Text' },
      { name: 'DC.source', content: 'Archivos Latinoamericanos de Estudios Trans' },
      { name: 'DC.language', content: 'es' },
    ];
    dcMetas.forEach(({ name, content }) => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      elements.push(meta);
    });

    return () => { elements.forEach(el => el.remove()); };
  }, [article, journalInfo]);
  return null;
}

export default function ArticlePage() {
  const { id } = useParams();
  const { user, loading: authLoading, canDownload, getSubscriptionStatus, getTrialDaysRemaining } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journalInfo, setJournalInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadArticle = async () => {
    setDownloading(true);
    try {
      await articlesAPI.downloadBlob(article.id);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar el articulo. Intenta de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    articlesAPI.get(id).then(res => setArticle(res.data)).catch(() => {}).finally(() => setLoading(false));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/journal-info`).then(r => r.json()).then(setJournalInfo).catch(() => {});
  }, [id]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;
  if (!article) return <div className="text-center py-24"><p className="text-gray-400 font-sans">Articulo no encontrado</p></div>;

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="article-page" itemScope itemType="http://schema.org/ScholarlyArticle">
      <ScholarMeta article={article} journalInfo={journalInfo} />

      {/* Back link */}
      <Link to="/archivo" className="inline-flex items-center gap-1 text-sm font-sans text-gray-400 hover:text-slate-dark mb-8 transition-colors" data-testid="article-back-link">
        <ArrowLeft className="w-4 h-4" /> Volver al archivo
      </Link>

      {/* Article Header */}
      <header className="animate-fade-up">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge variant="outline" className="text-xs font-sans bg-blue-50 text-blue-700 border-blue-100">
            Volumen {article.volumen}, Numero {article.numero}
          </Badge>
          {article.categoria && (
            <Badge variant="outline" className="text-xs font-sans bg-pink-50 text-pink-700 border-pink-100">
              {article.categoria}
            </Badge>
          )}
          {article.featured && (
            <Badge className="text-xs font-sans bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50" data-testid="article-featured-badge">
              <Star className="w-3 h-3 mr-1" fill="currentColor" /> Destacado
            </Badge>
          )}
          <RatingSummary avg={article.rating_avg} count={article.rating_count} testId="article-header-rating" />
        </div>

        <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-dark leading-tight" itemProp="headline">
          {article.titulo}
        </h1>

        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm font-sans text-gray-500">
          <span itemProp="author">{article.autores}</span>
          {article.published_at && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              <time dateTime={article.published_at} itemProp="datePublished">
                {new Date(article.published_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
              </time>
            </span>
          )}
        </div>

        {journalInfo?.issn && (
          <div className="flex items-center gap-1 mt-2 text-sm font-sans text-gray-400">
            ISSN: <span className="text-gray-600">{journalInfo.issn}</span>
          </div>
        )}

        {article.doi && (
          <div className="flex items-center gap-1 mt-2 text-sm font-sans text-gray-400">
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
            DOI: <a href={`https://doi.org/${article.doi}`} target="_blank" rel="noopener noreferrer" className="text-trans-blue hover:underline" itemProp="sameAs">{article.doi}</a>
          </div>
        )}

        {/* Trans flag accent */}
        <div className="flex items-center gap-1 mt-6">
          <div className="h-0.5 w-16 bg-trans-blue rounded-full" />
          <div className="h-0.5 w-16 bg-trans-pink rounded-full" />
          <div className="h-0.5 w-16 bg-gray-200 rounded-full" />
          <div className="h-0.5 w-16 bg-trans-pink rounded-full" />
          <div className="h-0.5 w-16 bg-trans-blue rounded-full" />
        </div>
      </header>

      {/* Keywords */}
      {article.palabras_clave?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8">
          <Tag className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
          {article.palabras_clave.map(kw => (
            <span key={kw} className="text-xs font-sans px-2.5 py-1 rounded-sm bg-gray-50 text-gray-600 border border-gray-100" itemProp="keywords">{kw}</span>
          ))}
        </div>
      )}

      {/* Audio Player */}
      {article.audio_name && (
        <section className="mt-8 p-5 bg-slate-dark/5 rounded-sm border border-gray-100" data-testid="article-audio-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-trans-blue/10 flex items-center justify-center">
              <Volume2 className="w-4 h-4 text-trans-blue" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-sans font-medium text-slate-dark">Escuchar articulo</p>
              <p className="text-xs font-sans text-gray-400">{article.audio_name}</p>
            </div>
          </div>
          <audio controls className="w-full" data-testid="article-audio-player">
            <source src={articlesAPI.audioUrl(article.id)} />
            Tu navegador no soporta audio HTML5.
          </audio>
        </section>
      )}

      {/* Abstract ES */}
      <section className="mt-10" data-testid="article-abstract-es">
        <h2 className="text-lg font-serif font-semibold text-slate-dark mb-3">Resumen</h2>
        <p className="font-serif text-gray-700 leading-[1.8]" itemProp="abstract">{article.abstract_es}</p>
      </section>

      {/* Abstract EN */}
      {article.abstract_en && (
        <section className="mt-8" data-testid="article-abstract-en">
          <h2 className="text-lg font-serif font-semibold text-slate-dark mb-3">Abstract</h2>
          <p className="font-serif text-gray-600 leading-[1.8] italic">{article.abstract_en}</p>
        </section>
      )}

      {/* Download */}
      {(article.file_available || article.file_name) && (
        <div className="mt-10 p-6 bg-gray-50 rounded-sm border border-gray-100" data-testid="article-download-section">
          {authLoading ? (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin w-5 h-5 border-2 border-trans-blue border-t-transparent rounded-full" />
            </div>
          ) : !user ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-gray-400 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-sans font-medium text-slate-dark text-sm">Registrate para descargar el articulo completo</p>
                  <p className="text-xs font-sans text-gray-400 mt-0.5">Acceso gratuito por 6 meses al registrarte</p>
                </div>
              </div>
              <Button asChild className="font-sans bg-slate-dark hover:bg-slate-dark/90 w-full sm:w-auto" data-testid="article-register-btn">
                <Link to="/registro">Registrarse</Link>
              </Button>
            </div>
          ) : getSubscriptionStatus() === 'expired' ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-trans-pink shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-sans font-medium text-slate-dark text-sm">Tu periodo de prueba ha expirado</p>
                  <p className="text-xs font-sans text-gray-400 mt-0.5">Suscribete por $499 MXN/ano para seguir descargando articulos</p>
                </div>
              </div>
              <Button asChild className="font-sans bg-trans-pink text-slate-dark hover:bg-trans-pink/90 w-full sm:w-auto" data-testid="article-subscribe-btn">
                <Link to="/suscripcion">Suscribirme</Link>
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-sans font-medium text-slate-dark text-sm">Descargar articulo completo</p>
                <p className="text-xs font-sans text-gray-400 mt-0.5">
                  {article.file_name}
                  {getSubscriptionStatus() === 'trial' && getTrialDaysRemaining() > 0 && (
                    <span className="ml-2 text-trans-blue">
                      <Clock className="w-3 h-3 inline mr-0.5" />{getTrialDaysRemaining()} dias restantes de prueba
                    </span>
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                className="font-sans"
                disabled={downloading}
                onClick={handleDownloadArticle}
                data-testid="article-download-btn"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Descargar PDF
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Reader rating & comments */}
      <ArticleFeedback articleId={article.id} />

      {/* Citation info for SEO */}
      <footer className="mt-12 pt-6 border-t border-gray-100">
        <p className="text-xs font-sans text-gray-400">
          <strong>Como citar:</strong> {article.autores}. ({article.published_at ? new Date(article.published_at).getFullYear() : ''}). {article.titulo}. <em>Archivos Latinoamericanos de Estudios Trans</em>, {article.volumen}({article.numero}).{article.doi ? ` https://doi.org/${article.doi}` : ''}{journalInfo?.issn ? ` ISSN: ${journalInfo.issn}` : ''}
        </p>
      </footer>
    </article>
  );
}
